import React, { useState, useEffect } from 'react';
import './CreatorProgressModal.css';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, getDoc, collection, addDoc, setDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import ChatWindow from '../Chat/ChatWindow';
import ChatService from '../Chat/ChatService';
import { useChat } from '../Chat/ChatContext';
import ShippingDetailsModal from './ShippingDetailsModal';
import ExtensionRequestModal from './ExtensionRequestModal';
import CancellationRequestModal from './CancellationRequestModal';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { getCreatorDueInfo } from '../utils/creatorDueUtils';
import toast from 'react-hot-toast';

const areProjectIdsEquivalent = (idA, idB) => {
  return ProjectIdGenerator.areEquivalent(idA, idB);
};

function CreatorProgressModal({ isOpen, onClose, creatorData, onScriptClick, setActiveTab }) {
  const [activePanel, setActivePanel] = useState('progress');
  const [showShippingDetailsModal, setShowShippingDetailsModal] = useState(false);
  const [showExtensionRequestModal, setShowExtensionRequestModal] = useState(false);
  const [showCancellationRequestModal, setShowCancellationRequestModal] = useState(false);
  const [freshCreatorData, setFreshCreatorData] = useState(null);
  const [assignedScripts, setAssignedScripts] = useState([]);
  const [hasSubmission, setHasSubmission] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);

  // Fetch fresh creator data from brief when modal opens (to get latest deliveryAddress)
  useEffect(() => {
    if (!isOpen || !creatorData) {
      setFreshCreatorData(null);
      setAssignedScripts([]);
      return;
    }

    // If briefId or campaignId are missing, still set freshCreatorData with available data
    if (!creatorData.briefId || !creatorData.campaignId) {
      setFreshCreatorData({
        ...creatorData,
        projectId: ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || creatorData.projectId || null,
        campaignName: creatorData.campaignName || creatorData.campaignId || 'Unknown Campaign',
        briefName: creatorData.briefName || creatorData.briefTitle || 'Untitled Brief',
        scriptCount: creatorData.scriptIds?.length || creatorData.scriptCount || 0
      });
      setAssignedScripts([]);
      return;
    }

    const fetchFreshData = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        // Fetch both campaign and brief documents in parallel
        const [campaignDoc, briefDoc] = await Promise.all([
          getDoc(doc(db, 'users', user.uid, 'campaigns', creatorData.campaignId)),
          getDoc(doc(db, 'users', user.uid, 'campaigns', creatorData.campaignId, 'briefs', creatorData.briefId))
        ]);
        
        if (!briefDoc.exists()) {
          console.warn('Brief not found for fresh data fetch, using original data');
          // Still set freshCreatorData with original data to ensure assignment details are available
          setFreshCreatorData({
            ...creatorData,
            projectId: ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || creatorData.projectId || null,
            campaignName: creatorData.campaignName || creatorData.campaignId || 'Unknown Campaign',
            briefName: creatorData.briefName || creatorData.briefTitle || 'Untitled Brief',
            scriptCount: creatorData.scriptIds?.length || creatorData.scriptCount || 0
          });
          setAssignedScripts([]);
          return;
        }

        const briefData = briefDoc.data();
        const campaignData = campaignDoc.exists() ? campaignDoc.data() : {};
        const creators = briefData.creators || [];
        
        // Get campaign name and brief name
        const campaignName = campaignData.name || creatorData.campaignName || creatorData.campaignId;
        const briefName = briefData.title || briefData.name || creatorData.briefName || 'Untitled Brief';
        
        // Find the exact creator by projectId - STRICT MATCHING REQUIRED
        // CRITICAL: With multiple assignments per creator, we MUST match by projectId only
        const targetProjectId = ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || null;
        
        if (!targetProjectId) {
          console.error('❌ CRITICAL: projectId is missing from creatorData. Cannot safely match creator entry.');
          // Still set data but warn user
          setFreshCreatorData({
            ...creatorData,
            projectId: null,
            campaignName: campaignName,
            briefName: briefName,
            scriptCount: creatorData.scriptIds?.length || 0,
            error: 'Project ID missing - cannot verify correct assignment'
          });
          setAssignedScripts([]);
          return;
        }
        
        // Check for multiple assignments - if found, we MUST use projectId matching
        const matchingCreators = creators.filter(c => c.id === creatorData.id);
        if (matchingCreators.length > 1) {
          console.log(`ℹ️ Multiple assignments found for creator ${creatorData.id}. Using strict projectId matching: ${targetProjectId}`);
        }
        
        // STRICT MATCHING: Only match by projectId - no fallback to creatorId-only matching
        // This ensures we get the correct project even if multiple assignments exist
        const creatorEntry = creators.find(c => {
          if (c.id !== creatorData.id) return false;
          if (!c.projectId) return false; // Skip entries without projectId
          return areProjectIdsEquivalent(c.projectId, targetProjectId);
        });
        
        if (!creatorEntry) {
          console.error(`❌ Creator entry not found for projectId: ${targetProjectId}`);
          // Check if creator exists but with different projectId
          const creatorExists = creators.some(c => c.id === creatorData.id);
          if (creatorExists) {
            const foundCreator = creators.find(c => c.id === creatorData.id);
            console.error(`⚠️ Creator found but projectId mismatch. Expected: ${targetProjectId}, Found: ${foundCreator.projectId || 'none'}`);
          }
          // Set error state
          setFreshCreatorData({
            ...creatorData,
            projectId: targetProjectId,
            campaignName: campaignName,
            briefName: briefName,
            scriptCount: creatorData.scriptIds?.length || 0,
            error: 'Creator entry not found for this project'
          });
          setAssignedScripts([]);
          return;
        }

        if (creatorEntry) {
          const normalizedEntryProjectId = ProjectIdGenerator.normalizeProjectId(creatorEntry.projectId || creatorData.projectId);
          
          // Get assigned scripts from brief
          const scriptIds = creatorEntry.scriptIds || creatorData.scriptIds || [];
          const briefScripts = briefData.scripts || [];
          const scripts = briefScripts
            .filter(s => scriptIds.some(id => String(s.id) === String(id)))
            .map(s => ({
              id: s.id,
              name: s.name || s.title || s.scriptName || 'Untitled Script',
              platform: s.settings?.platform || 'TikTok',
              videoLength: s.settings?.videoLength || 30,
              creativeControl: s.settings?.creativeControl || 'exact',
              additionalHooks: s.settings?.additionalHooks || 0,
              additionalCTAs: s.settings?.additionalCTAs || 0
            }));
          setAssignedScripts(scripts);

          // Merge fresh data with existing creatorData
          setFreshCreatorData({
            ...creatorData,
            projectId: normalizedEntryProjectId || creatorData.projectId || null,
            campaignName: campaignName,
            briefName: briefName,
            scriptCount: scripts.length,
            deliveryAddress: creatorEntry.deliveryAddress 
              || creatorEntry.progress?.deliveryAddress 
              || (creatorEntry.shippingDetails ? {
                addressLine1: creatorEntry.shippingDetails.addressLine1,
                addressLine2: creatorEntry.shippingDetails.addressLine2 || '',
                city: creatorEntry.shippingDetails.city,
                state: creatorEntry.shippingDetails.state || '',
                postalCode: creatorEntry.shippingDetails.postalCode,
                country: creatorEntry.shippingDetails.country,
                contactPerson: creatorEntry.shippingDetails.contactPerson || creatorEntry.name,
                phone: creatorEntry.shippingDetails.phone || '',
                specialInstructions: creatorEntry.shippingDetails.specialInstructions || ''
              } : creatorData.deliveryAddress),
            status: creatorEntry.status || creatorData.status,
            progress: creatorEntry.progress || creatorData.progress,
            deadline: creatorEntry.deadline || creatorData.deadline || null,
            graceRequest: creatorEntry.graceRequest || creatorData.graceRequest || null,
            extendedDeadline: creatorEntry.extendedDeadline || creatorData.extendedDeadline || null
          });
        } else {
          // Creator not found in brief, use original data but still include fresh campaign/brief names
          setFreshCreatorData({
            ...creatorData,
            projectId: ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || creatorData.projectId || null,
            campaignName: campaignName,
            briefName: briefName,
            scriptCount: creatorData.scriptIds?.length || creatorData.scriptCount || 0
          });
          setAssignedScripts([]);
        }
      } catch (error) {
        console.error('Error fetching fresh creator data:', error);
        // Fallback to original data but ensure assignment details are preserved
        setFreshCreatorData({
          ...creatorData,
          projectId: ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || creatorData.projectId || null,
          campaignName: creatorData.campaignName || creatorData.campaignId || 'Unknown Campaign',
          briefName: creatorData.briefName || creatorData.briefTitle || 'Untitled Brief',
          scriptCount: creatorData.scriptIds?.length || creatorData.scriptCount || 0
        });
        setAssignedScripts([]);
      }
    };

    fetchFreshData();
  }, [isOpen, creatorData?.id, creatorData?.briefId, creatorData?.campaignId, creatorData?.projectId]);

  // Use fresh data if available, otherwise fall back to original creatorData
  const effectiveCreatorData = freshCreatorData || creatorData;
  const normalizedBriefId = (effectiveCreatorData?.briefId || '').trim();
  const resolvedProjectId = effectiveCreatorData?.projectId || creatorData?.projectId || '';
  const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(resolvedProjectId) || '';

  // Check for submissions in Firestore
  useEffect(() => {
    if (!isOpen || !effectiveCreatorData || !effectiveCreatorData.briefId || !effectiveCreatorData.campaignId) {
      setHasSubmission(false);
      return;
    }

    const checkForSubmission = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;

        const brandUserId = user.uid;
        const campaignId = effectiveCreatorData.campaignId;
        const briefId = effectiveCreatorData.briefId;
        const creatorId = effectiveCreatorData.id;

        // Check if there's a submission for this creator in this brief
        const submissionsRef = collection(
          db,
          'users',
          brandUserId,
          'campaigns',
          campaignId,
          'briefs',
          briefId,
          'submissions'
        );

        const submissionsSnapshot = await getDocs(submissionsRef);
        
        // Find the submission for this creator - match ONLY by projectId
        // This prevents conflicts when same brief and creator are used multiple times
        let foundSubmission = null;
        if (normalizedProjectId) {
          for (const submissionDoc of submissionsSnapshot.docs) {
            const submissionData = submissionDoc.data();
            const submissionProjectId = submissionData.projectId ? 
              (ProjectIdGenerator.normalizeProjectId(submissionData.projectId) || submissionData.projectId) : null;
            
            // Match ONLY by projectId (no creatorId matching)
            if (submissionProjectId) {
              try {
                if (areProjectIdsEquivalent(submissionProjectId, normalizedProjectId)) {
                  foundSubmission = {
                    id: submissionDoc.id,
                    ...submissionData,
                    campaignId,
                    briefId
                  };
                  break;
                }
              } catch (e) {
                // If comparison fails, continue
              }
            }
          }
        } else {
          console.warn(`⚠️ Creator ${creatorId} missing projectId - cannot match to submission`);
        }

        setHasSubmission(!!foundSubmission);
        setSubmissionData(foundSubmission);
      } catch (error) {
        console.error('Error checking for submission:', error);
        setHasSubmission(false);
      }
    };

    checkForSubmission();
  }, [isOpen, effectiveCreatorData?.id, effectiveCreatorData?.briefId, effectiveCreatorData?.campaignId, normalizedProjectId]);
  
  // Get effective display status helper - override to 'submitted' if progress shows submitted or submission exists
  const getEffectiveStatus = (creator) => {
    if (!creator) return 'awaiting_approval';
    const progress = creator.progress || {};
    
    // If there's a submission in Firestore, show as submitted
    if (hasSubmission) {
      return 'submitted';
    }
    
    // If progress shows submitted, override status to 'submitted' for display
    if (progress.submitted === true) {
      return 'submitted';
    }
    
    return creator.status || 'awaiting_approval';
  };
  
  // Get effective display status - override to 'submitted' if progress shows submitted or submission exists
  const displayStatus = getEffectiveStatus(effectiveCreatorData);
  const dueInfo = getCreatorDueInfo({
    ...effectiveCreatorData,
    hasSubmission: hasSubmission || effectiveCreatorData?.hasSubmission,
    submissionData: submissionData || effectiveCreatorData?.submissionData,
  });

  useEffect(() => {
    if (isOpen && !normalizedProjectId) {
      console.error('CreatorProgressModal: missing projectId for chat initialization');
    }
  }, [isOpen, normalizedProjectId]);

  // Check if we should open chat panel automatically
  useEffect(() => {
    console.log('CreatorProgressModal useEffect:', { isOpen, openChatPanel: effectiveCreatorData?.openChatPanel, effectiveCreatorData });
    if (isOpen && effectiveCreatorData?.openChatPanel) {
      console.log('Setting active panel to chat');
      setActivePanel('chat');
    }
  }, [isOpen, effectiveCreatorData?.openChatPanel]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [chatStatus, setChatStatus] = useState('active');
  const { getChatForProject, getUnreadCount, chats } = useChat();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const isChatPanelActive = activePanel === 'chat';
  const hasNormalizedProjectId = Boolean(normalizedProjectId);
  const isChatReady = Boolean(chatId);
  const isWaitingForChat = isChatPanelActive && hasNormalizedProjectId && isLoading;
  const chatWindowProjectId = normalizedProjectId || chatId || '';

  // Helper function to get a valid image URL
  const getValidImageUrl = (avatar, profileImage) => {
    const fallback = '/creatorimages/creatorpic1v2-min.png';
    
    if (avatar && avatar !== 'null' && avatar !== 'undefined' && avatar.trim() !== '') {
      return avatar;
    }
    if (profileImage && profileImage !== 'null' && profileImage !== 'undefined' && profileImage.trim() !== '') {
      return profileImage;
    }
    return fallback;
  };

  // Load chat when modal opens
  useEffect(() => {
    console.log('=== CREATOR PROGRESS MODAL CHAT LOADING ===');
    console.log('isOpen:', isOpen);
    console.log('creatorData:', JSON.stringify(creatorData, null, 2));
    console.log('briefId:', creatorData?.briefId);
    console.log('openChatPanel:', creatorData?.openChatPanel);
    
    if (!isOpen || !effectiveCreatorData || !normalizedProjectId) {
      console.log('Setting chatId to null - missing required data');
      console.log('Missing:', { isOpen: !isOpen, creatorData: !effectiveCreatorData, projectId: !normalizedProjectId });
      setChatId(null);
      setIsLoading(false);
      return;
    }

    const loadChat = async () => {
      setIsLoading(true);
      try {
        // If we have an existing chatId from the notification, use it directly
        if (effectiveCreatorData.existingChatId) {
          console.log('Using chatId from notification:', effectiveCreatorData.existingChatId);
          setChatId(effectiveCreatorData.existingChatId);
          setChatStatus('active');
          return;
        }

        // Try to get existing chat for this brief
        const existingChat = getChatForProject(normalizedProjectId);
        console.log('Existing chat found:', existingChat);

        if (existingChat) {
          console.log('Using existing chat:', existingChat.chatId);
          setChatId(existingChat.chatId);
          setChatStatus(existingChat.status || 'active');
        } else {
          // Check if a chat should exist (creator status is confirmed or later)
          const shouldHaveChat = ['confirmed', 'accepted', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted', 'needs_revision', 'in_progress', 'completed'].includes(effectiveCreatorData.status);
          console.log('Should have chat?', shouldHaveChat, 'Status:', effectiveCreatorData.status);

          if (shouldHaveChat && currentUser && normalizedProjectId) {
            console.log('Creating new chat...');
            // Create chat if it doesn't exist
            const newChatId = await ChatService.createChat({
              brandUserId: currentUser.uid,
              creatorId: effectiveCreatorData.id,
              briefId: normalizedBriefId,
              campaignId: effectiveCreatorData.campaignId,
              brandName: currentUser.displayName || 'Brand',
              creatorName: effectiveCreatorData.name || effectiveCreatorData.creator,
              briefName: effectiveCreatorData.briefName,
              campaignName: effectiveCreatorData.campaignName,
              projectId: normalizedProjectId || effectiveCreatorData.projectId
            });
            console.log('New chat created:', newChatId);
            setChatId(newChatId);
            setChatStatus('active');
          } else {
            console.log('Not creating chat - shouldHaveChat:', shouldHaveChat, 'currentUser:', !!currentUser);
          }
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadChat();
  }, [isOpen, creatorData, getChatForProject, currentUser, normalizedBriefId, normalizedProjectId, chats]);

  // Auto-open shipping details modal when requested from creator card
  useEffect(() => {
    if (isOpen && creatorData?.openShippingDetails) {
      setTimeout(() => {
        setShowShippingDetailsModal(true);
      }, 300);
    }
  }, [isOpen, effectiveCreatorData?.openShippingDetails]);

  // Auto-open extension request modal when requested from creator card
  useEffect(() => {
    if (isOpen && creatorData?.openExtensionRequest) {
      console.log('🔵 Opening extension request modal:', {
        hasEffectiveCreatorData: !!effectiveCreatorData,
        projectId: effectiveCreatorData?.projectId,
        creatorId: effectiveCreatorData?.id,
        hasGraceRequest: !!effectiveCreatorData?.graceRequest,
        graceRequestStatus: effectiveCreatorData?.graceRequest?.status
      });
      setTimeout(() => {
        setShowExtensionRequestModal(true);
      }, 300);
    }
  }, [isOpen, creatorData?.openExtensionRequest, effectiveCreatorData]);

  // Auto-open cancellation request modal when requested from creator card
  useEffect(() => {
    if (isOpen && creatorData?.openCancellationRequest) {
      console.log('🔴 Opening cancellation request modal:', {
        hasEffectiveCreatorData: !!effectiveCreatorData,
        projectId: effectiveCreatorData?.projectId,
        creatorId: effectiveCreatorData?.id,
        hasCancellationRequest: !!effectiveCreatorData?.cancellationRequest,
        cancellationRequestStatus: effectiveCreatorData?.cancellationRequest?.status
      });
      setTimeout(() => {
        setShowCancellationRequestModal(true);
      }, 300);
    }
  }, [isOpen, creatorData?.openCancellationRequest, effectiveCreatorData]);
  
  if (!isOpen || !effectiveCreatorData) return null;


  const getStatusColor = (status) => {
    switch (status) {
      case 'awaiting_approval': return '#f59e0b';
      case 'accepted':
      case 'confirmed': return '#10b981';
      case 'awaiting_product': return '#3b82f6';
      case 'filming': return '#42bcff';
      case 'editing': return '#42bcff';
      case 'submitted': return '#5cd68d';
      case 'rejected': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getStatusBackgroundColor = (status, isOverdue) => {
    if (isOverdue) return '#dc2626'; // Red for overdue
    switch (status) {
      case 'awaiting_approval': return '#f59e0b'; // Orange for awaiting approval
      case 'accepted':
      case 'confirmed': return '#10b981'; // Green for confirmed
      case 'awaiting_product': return '#3b82f6'; // Blue for awaiting product
      case 'filming':
      case 'editing':
        return '#42bcff'; // Blue for filming/editing
      case 'submitted':
        return '#5cd68d'; // Green for submitted
      case 'rejected':
        return '#ef4444'; // Red for rejected
      default:
        return '#64748b'; // Gray for default
    }
  };

  const getStatusTextColor = (status, isOverdue) => {
    return 'white'; // Always white for better visibility
  };

  const getStatusDotColor = (status, isOverdue) => {
    if (isOverdue) return 'white'; // White dot for red background
    switch (status) {
      case 'filming':
      case 'editing':
        return 'white'; // White dot for yellow background
      case 'review':
      case 'awaiting':
        return 'white'; // White dot for blue background
      case 'submitted':
        return 'white'; // White dot for green background
      default:
        return 'white'; // White dot for gray background
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'awaiting_approval': return 'Awaiting Approval';
      case 'accepted': return 'Accepted';
      case 'confirmed': return 'Confirmed';
      case 'awaiting_product': return 'Awaiting Product';
      case 'product_sent': return 'Product Sent';
      case 'filming': return 'Filming';
      case 'editing': return 'Editing';
      case 'submitted': return 'Submitted';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const getStatusCategory = (status, isOverdue) => {
    if (isOverdue) return 'Overdue';
    switch (status) {
      case 'awaiting_approval': return 'Pending Approval';
      case 'accepted':
      case 'confirmed': return 'Awaiting Product';
      case 'awaiting_product': return 'Awaiting Product';
      case 'product_sent': return 'Product Sent';
      case 'filming':
      case 'editing':
        return 'In Progress';
      case 'submitted':
        return 'Ready to Review';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Status';
    }
  };

  const getStatusSubText = (status, statusText) => {
    switch (status) {
      case 'awaiting_approval': return 'Creator needs to accept assignment';
      case 'accepted':
      case 'confirmed': return 'Waiting for product to be shipped to creator';
      case 'awaiting_product': return 'Waiting for product to be shipped';
      case 'product_sent': return 'Product has been shipped and is on its way';
      case 'filming': return 'Creator is currently filming';
      case 'editing': return 'Creator is editing the content';
      case 'submitted': return 'Work has been submitted for review';
      case 'rejected': return 'Creator has rejected the assignment';
      default:
        return statusText || status;
    }
  };

  const getProgressStages = (creator) => {
    const progress = creator.progress || {};
    // Accepted: completed once creator has accepted/confirmed or moved beyond
    const acceptedCompleted = ['accepted', 'confirmed', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted'].includes(creator.status);
    // Received: for services always true; for products only when creator marked as received
    const receivedCompleted = effectiveCreatorData.productType === 'service'
      ? true
      : (progress.productReceived || false);
    // Enforce logical order: later steps only show completed if earlier steps are completed.
    // This fixes the case where the same creator is reassigned to the same brief and stale
    // progress (e.g. filming/editing true from a previous assignment) would show all ticks
    // while status correctly showed "Received" as current.
    const filmingCompleted = receivedCompleted && (
      progress.filming || ['editing', 'submitted'].includes(creator.status)
    );
    const editingCompleted = filmingCompleted && (
      progress.editing || creator.status === 'submitted'
    );

    const stages = [
      { key: 'accepted', label: 'Accepted', completed: acceptedCompleted },
      { key: 'product', label: 'Received', completed: receivedCompleted },
      { key: 'filming', label: 'Filming', completed: filmingCompleted },
      { key: 'editing', label: 'Editing', completed: editingCompleted }
    ];
    return stages;
  };

  const getStepperState = (creator) => {
    const stages = getProgressStages(creator);
    const activeStageIndex = stages.findIndex(stage => !stage.completed);
    const allCompleted = stages.every(stage => stage.completed);
    return { stages, activeStageIndex, allCompleted };
  };

  // Get creator status (Pro or Rising Star)
  const getCreatorStatus = (creator) => {
    // Check if creator has tier data
    if (creator.tier) {
      const isPro = creator.tier === 'Pro';
      return {
        type: isPro ? 'pro' : 'rising',
        text: creator.tier,
        class: isPro ? 'creator-status-pro' : 'creator-status-rising'
      };
    }
    
    // Fallback: Default to Rising Star if no tier data
    return {
      type: 'rising',
      text: 'Rising Star',
      class: 'creator-status-rising'
    };
  };


  const getProgressPercentage = (creator) => {
    const stages = getProgressStages(creator);
    const completedStages = stages.filter(stage => stage.completed).length;
    return (completedStages / stages.length) * 100;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleViewSubmission = () => {
    // Navigate to submissions page
    // You can replace this with your actual navigation logic
    console.log('Navigating to submissions page for:', creatorData.creator);
    // Example: window.location.href = `/submissions/${creatorData.id}`;
    // Or use React Router: navigate(`/submissions/${creatorData.id}`);
    onClose(); // Close modal after navigation
  };

  const handleApproveGrace = async () => {
    if (!creatorData.graceRequest || isLoading) return;
    
    setIsLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Validate projectId exists
      if (!creatorData.projectId) {
        console.error('❌ Missing projectId in creatorData:', creatorData);
        throw new Error('Project ID is required. Cannot process extension request without project ID.');
      }

      const { campaignId, briefId } = creatorData;
      const briefRef = doc(db, 'users', user.uid, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Find creator by strict projectId matching
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(creatorData.projectId);
      let creatorFound = false;
      
      const updatedCreators = creators.map(c => {
        const creatorProjectId = c.projectId || c.progress?.projectId || null;
        const creatorNormalizedProjectId = creatorProjectId ? ProjectIdGenerator.normalizeProjectId(creatorProjectId) : null;
        const matchesCreator = c.id === creatorData.id;
        
        // Strict matching: must match both creatorId AND projectId
        const matchesProject = normalizedProjectId && creatorNormalizedProjectId
          ? ProjectIdGenerator.areEquivalent(creatorNormalizedProjectId, normalizedProjectId)
          : false;
        
        if (matchesCreator && matchesProject) {
          creatorFound = true;
          const originalDeadline = new Date(c.deadline);
          const extendedDeadline = new Date(originalDeadline.getTime() + (c.graceRequest.requestedDays * 24 * 60 * 60 * 1000));

          return {
            ...c,
            graceRequest: {
              ...c.graceRequest,
              status: 'approved',
              respondedAt: new Date().toISOString(),
              approvedDays: c.graceRequest.requestedDays
            },
            extendedDeadline: extendedDeadline.toISOString()
          };
        }
        return c;
      });

      if (!creatorFound) {
        console.error('❌ Creator not found in brief for extension approval:', {
          creatorId: creatorData.id,
          projectId: creatorData.projectId,
          normalizedProjectId,
          campaignId: creatorData.campaignId,
          briefId: creatorData.briefId
        });
        throw new Error('Creator assignment not found in brief');
      }

      await updateDoc(briefRef, { creators: updatedCreators });

      // Notify creator
      const notification = {
        type: 'grace_approved',
        title: 'Extension Approved',
        message: `Your ${creatorData.graceRequest.requestedDays} day extension has been approved`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorData.id,
        creatorId: creatorData.id,
        projectId: creatorData.projectId || null,
        briefId: creatorData.briefId || null,
        campaignId: creatorData.campaignId || null,
        details: {
          brandUserId: user.uid,
          projectId: creatorData.projectId || null,
          campaignId: creatorData.campaignId || null,
          campaignName: creatorData.campaignName,
          briefId: creatorData.briefId || null,
          briefName: creatorData.briefName,
          approvedDays: creatorData.graceRequest.requestedDays,
          approvedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const creatorNotificationsRef = collection(db, 'creators', creatorData.id, 'notifications');
      await addDoc(creatorNotificationsRef, notification);

      onClose(); // Close and let parent refresh
    } catch (error) {
      console.error('Error approving grace:', error);
      const errorMessage = error.message === 'Creator assignment not found in brief' 
        ? 'Project not found. Please refresh the page and try again.'
        : 'Failed to approve extension request. Please try again.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDenyGrace = async () => {
    if (!creatorData.graceRequest || isLoading) return;
    
    setIsLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Validate projectId exists
      if (!creatorData.projectId) {
        console.error('❌ Missing projectId in creatorData:', creatorData);
        throw new Error('Project ID is required. Cannot process extension request without project ID.');
      }

      const { campaignId, briefId } = creatorData;
      const briefRef = doc(db, 'users', user.uid, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Find creator by strict projectId matching
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(creatorData.projectId);
      let creatorFound = false;
      
      const updatedCreators = creators.map(c => {
        const creatorProjectId = c.projectId || c.progress?.projectId || null;
        const creatorNormalizedProjectId = creatorProjectId ? ProjectIdGenerator.normalizeProjectId(creatorProjectId) : null;
        const matchesCreator = c.id === creatorData.id;
        
        // Strict matching: must match both creatorId AND projectId
        const matchesProject = normalizedProjectId && creatorNormalizedProjectId
          ? ProjectIdGenerator.areEquivalent(creatorNormalizedProjectId, normalizedProjectId)
          : false;
        
        if (matchesCreator && matchesProject) {
          creatorFound = true;
          return {
            ...c,
            graceRequest: {
              ...c.graceRequest,
              status: 'denied',
              respondedAt: new Date().toISOString()
            }
          };
        }
        return c;
      });

      if (!creatorFound) {
        console.error('❌ Creator not found in brief for extension denial:', {
          creatorId: creatorData.id,
          projectId: creatorData.projectId,
          normalizedProjectId,
          campaignId: creatorData.campaignId,
          briefId: creatorData.briefId
        });
        throw new Error('Creator assignment not found in brief');
      }

      await updateDoc(briefRef, { creators: updatedCreators });

      // Notify creator
      const notification = {
        type: 'grace_denied',
        title: 'Extension Not Approved',
        message: `Your extension request for "${creatorData.briefName}" was not approved`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorData.id,
        creatorId: creatorData.id,
        projectId: creatorData.projectId || null,
        briefId: creatorData.briefId || null,
        campaignId: creatorData.campaignId || null,
        details: {
          brandUserId: user.uid,
          projectId: creatorData.projectId || null,
          campaignId: creatorData.campaignId || null,
          campaignName: creatorData.campaignName,
          briefId: creatorData.briefId || null,
          briefName: creatorData.briefName,
          deniedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const creatorNotificationsRef = collection(db, 'creators', creatorData.id, 'notifications');
      await addDoc(creatorNotificationsRef, notification);

      onClose(); // Close and let parent refresh
    } catch (error) {
      console.error('Error denying grace:', error);
      const errorMessage = error.message === 'Creator assignment not found in brief' 
        ? 'Project not found. Please refresh the page and try again.'
        : 'Failed to reject extension request. Please try again.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductSent = async (creatorData, trackingDetails, deliveryCompany) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const { campaignId, briefId } = creatorData;
      const briefRef = doc(db, 'users', user.uid, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) throw new Error('Brief not found');

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];

      // Resolve the exact projectId for this shipment to avoid updating sibling assignments
      // CRITICAL: We MUST have a projectId to avoid updating the wrong assignment when multiple exist
      // First try from creatorData, then try to find it from the brief's creator entry
      let targetProjectId = ProjectIdGenerator.normalizeProjectId(creatorData?.projectId) || null;
      
      // If projectId is missing from creatorData, try to find it from the brief
      if (!targetProjectId) {
        const creatorEntry = creators.find(c => c.id === creatorData.id);
        if (creatorEntry?.projectId) {
          targetProjectId = ProjectIdGenerator.normalizeProjectId(creatorEntry.projectId);
        }
      }
      
      
      if (!targetProjectId) {
        const matchingCreators = creators.filter(c => c.id === creatorData.id);
        if (matchingCreators.length > 1) {
          throw new Error('Cannot update shipping details: Multiple assignments found for this creator but projectId is missing. Cannot determine which assignment to update.');
        } else if (matchingCreators.length === 1 && !matchingCreators[0].projectId) {
          throw new Error('Cannot update shipping details: Assignment found but projectId is missing. This may be an old assignment without a projectId.');
        } else {
          throw new Error('Cannot update shipping details: Creator not found in brief or projectId is missing.');
        }
      }

      // Update only the matching assignment (same creator id AND same projectId - STRICT MATCHING)
      const updatedCreators = creators.map(c => {
        const matchesCreator = c.id === creatorData.id;
        // Require projectId match - no fallback to creatorId-only matching
        const matchesProject = targetProjectId && c.projectId 
          ? areProjectIdsEquivalent(c.projectId, targetProjectId) 
          : false;
        
        if (matchesCreator && matchesProject) {
          return {
            ...c,
            status: 'product_sent',
            productSentAt: new Date().toISOString(),
            progress: {
              ...c.progress,
              productSent: true,
              trackingNumber: trackingDetails || null,
              deliveryCompany: deliveryCompany || null,
              trackingInfo: trackingDetails || 'No tracking number provided'
            }
          };
        }
        return c;
      });

      await updateDoc(briefRef, { creators: updatedCreators });

        // Also write a shipping update to a separate creator root so the creator can see it without changing project lists
        try {
          // First verify the brief exists and the creator is assigned
          const briefRef = doc(db, 'users', user.uid, 'campaigns', creatorData.campaignId, 'briefs', creatorData.briefId);
          const briefDoc = await getDoc(briefRef);
          
          if (!briefDoc.exists()) {
            console.error('🚢 Brief not found:', creatorData.briefId);
            throw new Error('Brief not found');
          }
          
          const briefData = briefDoc.data();
          const creatorIds = briefData.creatorIds || [];
          
          if (!creatorIds.includes(creatorData.id)) {
            console.error('🚢 Creator not assigned to brief:', creatorData.id, 'not in', creatorIds);
            throw new Error('Creator not assigned to brief');
          }
          
          // Reuse the assigned projectId from the brief's creator entry (do NOT generate a new one at ship time)
          const briefCreators = briefData.creators || [];
          const creatorEntry = briefCreators.find((c) => c.id === creatorData.id && (!targetProjectId || areProjectIdsEquivalent(c.projectId, targetProjectId))) || {};
          const effectiveProjectId = ProjectIdGenerator.normalizeProjectId(creatorEntry.projectId || creatorData.projectId);
          if (!effectiveProjectId) {
            throw new Error('Missing projectId for shipment; refusing to generate a new one.');
          }
          const shippingDocId = effectiveProjectId;
          const shippingDocRef = doc(db, 'creators', creatorData.id, 'shipping', shippingDocId);
          const shippingData = {
            briefId: creatorData.briefId,
            campaignId: creatorData.campaignId,
            brandUserId: user.uid,
            creatorId: creatorData.id,
            projectId: effectiveProjectId,
            shippedAt: new Date().toISOString(),
            trackingNumber: trackingDetails || null,
            deliveryCompany: deliveryCompany || null
          };
          
          console.log('🚢 Writing shipping doc to:', `creators/${creatorData.id}/shipping/${shippingDocId}`);
          console.log('🚢 Shipping data:', shippingData);
          console.log('🚢 Shipping doc id (projectId):', shippingDocId);
          console.log('🚢 Brief verification:', {
            briefExists: briefDoc.exists(),
            creatorAssigned: creatorIds.includes(creatorData.id),
            creatorIds: creatorIds
          });
          
          // Verify Firestore rule requirements
          const expectedDocId = `${user.uid}_${creatorData.id}_${creatorData.briefId}`;
          const hasRequiredFields = ['campaignId', 'briefId', 'brandUserId', 'creatorId', 'shippedAt'].every(field => 
            shippingData.hasOwnProperty(field) && shippingData[field] !== null && shippingData[field] !== undefined
          );
          
          console.log('🚢 Firestore rule verification:', {
            expectedDocId,
            actualDocId: shippingDocId,
            docIdMatches: true,
            hasRequiredFields,
            requiredFields: ['campaignId', 'briefId', 'brandUserId', 'creatorId', 'shippedAt'],
            actualFields: Object.keys(shippingData),
            creatorIdMatches: shippingData.creatorId === creatorData.id,
            brandUserIdMatches: shippingData.brandUserId === user.uid,
            effectiveProjectId
          });
          
          console.log('🚢 Attempting to write shipping document...');
          await setDoc(shippingDocRef, shippingData, { merge: true });
          console.log('🚢 Shipping doc written successfully');

          // PROJECTS single-source-of-truth (optional - may fail due to Firestore rules)
          try {
            const projectDocRef = doc(db, 'projects', effectiveProjectId);
            const projectShipping = {
              projectId: effectiveProjectId,
              brandUserId: user.uid,
              creatorId: creatorData.id,
              campaignId: creatorData.campaignId,
              briefId: creatorData.briefId,
              status: 'product_sent',
              shippedAt: shippingData.shippedAt,
              trackingNumber: shippingData.trackingNumber,
              deliveryCompany: shippingData.deliveryCompany,
            };
            await setDoc(projectDocRef, projectShipping, { merge: true });
            console.log('📦 Project-level shipping written to projects/', effectiveProjectId);
          } catch (e) {
            // Silently fail - projects collection may not be accessible due to Firestore rules
            // The creators/{creatorId}/shipping write above is sufficient
            console.warn('⚠️ Could not write to projects collection (may require Firestore rules update):', e.message);
          }
        } catch (e) {
          console.error('Failed to write shipping update doc:', e);
          console.error('Error details:', {
            code: e.code,
            message: e.message,
            shippingDocId: `${user.uid}_${creatorData.id}_${creatorData.briefId}`,
            shippingData: {
              briefId: creatorData.briefId,
              campaignId: creatorData.campaignId,
              brandUserId: user.uid,
              creatorId: creatorData.id,
              shippedAt: new Date().toISOString()
            }
          });
        }

      // Reuse the same projectId used for the shipping document
      const projectId = (() => {
        try {
          const briefCreators = briefData?.creators || [];
          const entry = briefCreators.find((c) => c.id === creatorData.id);
          return (entry && ProjectIdGenerator.normalizeProjectId(entry.projectId))
            || ProjectIdGenerator.normalizeProjectId(creatorData.projectId)
            || creatorData.projectId
            || null;
        } catch (e) {
          return ProjectIdGenerator.normalizeProjectId(creatorData.projectId) || creatorData.projectId || null;
        }
      })();

      // Notify creator
      const normalizedNotificationProjectId = ProjectIdGenerator.normalizeProjectId(projectId) || projectId;
      const notification = {
        type: 'product_sent',
        title: 'Product Shipped!',
        message: `Your product for "${creatorData.briefName}" has been shipped and is on its way!`,
        // REQUIRED fields for notification routing and security
        recipientId: creatorData.id,
        creatorId: creatorData.id,
        projectId: normalizedNotificationProjectId,
        briefId: creatorData.briefId,
        campaignId: creatorData.campaignId,
        details: {
          projectId: normalizedNotificationProjectId,
          assignedBy: user.uid, // Required for Firestore rule
          briefId: creatorData.briefId,
          campaignId: creatorData.campaignId,
          campaignName: creatorData.campaignName,
          briefName: creatorData.briefName,
          shippedAt: new Date().toISOString(),
          trackingNumber: trackingDetails || null,
          deliveryCompany: deliveryCompany || null,
          trackingInfo: trackingDetails || 'No tracking number provided'
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      // Create shipping notification (simplified for now)
      try {
        console.log('🔔 Creating shipping notification...');
        const creatorNotificationsRef = collection(db, 'creators', creatorData.id, 'notifications');
        await addDoc(creatorNotificationsRef, notification);
        console.log('🔔 Shipping notification created successfully');
        toast.success('Product marked as sent! Creator has been notified.', {
          icon: '📦',
          duration: 4000,
        });
      } catch (notificationError) {
        console.error('🔔 Error with notification:', notificationError);
        // Don't fail the whole operation if notification fails
        toast('Product marked as sent! (Notification may not have been sent)', {
          icon: '⚠️',
          duration: 4000,
        });
      }
      
      // Close modal to refresh data
      onClose();
    } catch (error) {
      console.error('Error marking product as sent:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Failed to mark product as sent: ${errorMessage}`, {
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="creator-progress-modal-overlay" onClick={handleBackdropClick}>
      <div className="creator-progress-modal split-modal">
        {isChatPanelActive ? (
          isChatReady ? (
            /* Chat Panel */
            <div className="modal-chat-panel">
              <div className="chat-panel-header">
                <button className="back-to-progress-btn" onClick={() => setActivePanel('progress')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back to Progress
                </button>
                <button className="close-button" onClick={onClose}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <ChatWindow
                chatId={chatId}
                projectId={chatWindowProjectId}
                briefId={normalizedBriefId}
                campaignId={effectiveCreatorData.campaignId}
                creatorId={effectiveCreatorData.id}
                creatorName={effectiveCreatorData.name || effectiveCreatorData.creator}
                creatorHandle={effectiveCreatorData.handle || effectiveCreatorData.username || effectiveCreatorData.creator}
                creatorAvatar={effectiveCreatorData.avatar || effectiveCreatorData.profileImage}
                briefName={effectiveCreatorData.briefName}
                campaignName={effectiveCreatorData.campaignName}
                chatStatus={chatStatus}
              />
            </div>
          ) : (
            <div className="modal-chat-panel">
              <div className="chat-panel-header">
                <button className="back-to-progress-btn" onClick={() => setActivePanel('progress')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back to Progress
                </button>
                <button className="close-button" onClick={onClose}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {isWaitingForChat ? (
                <div className="creator-progress-chat-loading">
                  <div className="creator-progress-chat-loading-spinner" />
                  <p>Setting up chat for this project...</p>
                </div>
              ) : (
                <div className="creator-progress-chat-loading">
                  <p>Chat is unavailable for this assignment.</p>
                </div>
              )}
            </div>
          )
        ) : (
          /* Progress Panel */
          <>
            <div className="modal-header">
          <div className="creator-progress-info-header">
            <div className="creator-progress-avatar-container">
              <img src={getValidImageUrl(effectiveCreatorData.avatar, effectiveCreatorData.profileImage)} alt={effectiveCreatorData.creator} className="creator-progress-avatar" onError={(e) => { e.target.src = '/creatorimages/creatorpic1v2-min.png'; }} />
            </div>
            <div className="creator-progress-details">
              <h2 className="creator-progress-name">{effectiveCreatorData.name}</h2>
              <p className="creator-progress-handle">{effectiveCreatorData.creator}</p>
              <div className={`creator-status-tag ${getCreatorStatus(effectiveCreatorData).class}`}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                </svg>
                {getCreatorStatus(effectiveCreatorData).text}
              </div>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-content">

          {/* Creator & Assignment Info */}
          <div className="assignment-section">
            <div className="assignment-header">
              <h3 className="section-title">Assignment Details</h3>
            </div>
            
            <div className="assignment-grid">
              <div className="assignment-card">
                <div className="card-header card-header-campaign">
                  <h4 className="card-title">Campaign</h4>
                </div>
                <div className="card-content">
                  <p className="assignment-text">{effectiveCreatorData.campaignName}</p>
                </div>
              </div>
              
              <div className="assignment-card">
                <div className="card-header card-header-brief">
                  <h4 className="card-title">Brief</h4>
                </div>
                <div className="card-content">
                  <p className="assignment-text">{effectiveCreatorData.briefName || 'No brief assigned'}</p>
                </div>
              </div>
              
              <div className="assignment-card">
                <div className="card-header card-header-scripts">
                  <h4 className="card-title">Scripts</h4>
                </div>
                <div className="card-content">
                  <p className="assignment-text">
                    {effectiveCreatorData.scriptCount > 0 
                      ? `${effectiveCreatorData.scriptCount} script${effectiveCreatorData.scriptCount > 1 ? 's' : ''} assigned`
                      : 'No scripts assigned'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Scripts Section */}
          {assignedScripts.length > 0 && (
            <div className="assigned-scripts-section">
              <h3 className="section-title">Assigned Scripts</h3>
              <div className="assigned-scripts-grid">
                {assignedScripts.map((script) => (
                  <div 
                    key={script.id} 
                    className={`assigned-script-card ${onScriptClick ? 'clickable' : ''}`}
                    onClick={() => {
                      if (onScriptClick) {
                        onScriptClick(script);
                      }
                    }}
                  >
                    <div className="assigned-script-name">{script.name}</div>
                    <div className="assigned-script-meta">
                      <span className="assigned-script-platform">{script.platform}</span>
                      <span className="assigned-script-separator">•</span>
                      <span className="assigned-script-length">{script.videoLength}s</span>
                      <span className="assigned-script-separator">•</span>
                      <span className="assigned-script-control">
                        {script.creativeControl === 'exact' ? 'Scripted' : 'Creative'}
                      </span>
                    </div>
                    {(script.additionalHooks > 0 || script.additionalCTAs > 0) && (
                      <div className="assigned-script-extras">
                        {script.additionalHooks > 0 && (
                          <span className="assigned-script-extra-item">
                            +{script.additionalHooks} Hook{script.additionalHooks !== 1 ? 's' : ''}
                          </span>
                        )}
                        {script.additionalCTAs > 0 && (
                          <span className="assigned-script-extra-item">
                            +{script.additionalCTAs} CTA{script.additionalCTAs !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress & Timeline */}
          <div className="progress-section">
            <div className="progress-header">
              <h3 className="section-title">Progress</h3>
              <span className="progress-stage">{getStatusCategory(displayStatus, dueInfo.isOverdue)}</span>
            </div>
            
            <div className="due-date-section">
              <span className={`due-date ${dueInfo.isOverdue ? 'overdue' : ''}`}>
                {dueInfo.dueLabel}
              </span>
            </div>
            

            <div className="timeline-section">
              <div className="progress-stages">
                {(() => {
                  const stepperState = getStepperState(effectiveCreatorData);
                  return stepperState.stages.map((stage, index) => {
                    const isCompleted = stage.completed;
                    const isActive = index === stepperState.activeStageIndex;
                    
                    return (
                      <div key={stage.key} className="timeline-item">
                        <div className={`timeline-dot ${isCompleted ? 'completed' : isActive ? 'active' : ''}`}></div>
                        <div className="timeline-content">
                          <h4 className={`stage-name ${isCompleted ? 'completed' : ''}`}>{stage.label}</h4>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div 
            className={`status-section ${displayStatus === 'submitted' && setActiveTab ? 'status-clickable' : ''}`}
            onClick={() => {
              if (displayStatus === 'submitted' && setActiveTab && submissionData) {
                // Navigate to Hand-In page with submission ID
                setActiveTab('handin', { openSubmissionId: submissionData.id, openCampaignId: submissionData.campaignId, openBriefId: submissionData.briefId });
                onClose();
              }
            }}
            style={{ cursor: displayStatus === 'submitted' && setActiveTab ? 'pointer' : 'default' }}
          >
            <div 
              className="status-header"
              style={{ 
                backgroundColor: getStatusBackgroundColor(displayStatus, dueInfo.isOverdue),
                color: getStatusTextColor(displayStatus, dueInfo.isOverdue)
              }}
            >
              <div 
                className="status-indicator"
                style={{ backgroundColor: getStatusBackgroundColor(displayStatus, dueInfo.isOverdue) }}
              >
                <span 
                  className="status-dot" 
                  style={{ backgroundColor: getStatusDotColor(displayStatus, dueInfo.isOverdue) }}
                ></span>
              </div>
              <div className="status-info">
                <h3 className="status-text">
                  {getStatusCategory(displayStatus, dueInfo.isOverdue)}
                  {displayStatus === 'submitted' && setActiveTab && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '8px', display: 'inline-block', verticalAlign: 'middle' }}>
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </h3>
                <p className="status-description">
                  {getStatusSubText(displayStatus, effectiveCreatorData.statusText)}
                  {displayStatus === 'submitted' && setActiveTab && ' • Click to review'}
                </p>
              </div>
            </div>
          </div>


          {/* Grace Request Notification */}
          {creatorData.graceRequest?.status === 'pending' && (
            <div className="grace-request-notification">
              <div className="grace-request-header">
                <div className="grace-request-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                </div>
                <div className="grace-request-info">
                  <h4>Extension Request</h4>
                  <p>{creatorData.name} requests {creatorData.graceRequest.requestedDays} day extension</p>
                  {creatorData.graceRequest.reason && (
                    <p className="grace-reason">"{creatorData.graceRequest.reason}"</p>
                  )}
                </div>
              </div>
              <div className="grace-request-actions">
                <button 
                  className="grace-deny-btn"
                  onClick={() => handleDenyGrace()}
                >
                  Deny
                </button>
                <button 
                  className="grace-approve-btn"
                  onClick={() => handleApproveGrace()}
                >
                  Approve
                </button>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <div className="corner-actions">
              {/* Chat button - only enabled for confirmed creators - on the left */}
              {['accepted', 'confirmed', 'awaiting_product', 'product_sent', 'filming', 'editing', 'submitted', 'needs_revision', 'in_progress', 'completed'].includes(effectiveCreatorData.status) && (chatId || normalizedProjectId) && (
                <button 
                  className={`action-button chat-button ${activePanel === 'chat' ? 'active' : ''}`}
                  onClick={() => setActivePanel(activePanel === 'chat' ? 'progress' : 'chat')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {activePanel === 'chat' ? 'Progress' : 'Chat'}
                  {getUnreadCount(chatId) > 0 && activePanel !== 'chat' && (
                    <span className="notification-dot">{getUnreadCount(chatId)}</span>
                  )}
                </button>
              )}

              {/* Action buttons based on status - on the right */}
              <div className="right-actions">
                {effectiveCreatorData.status === 'awaiting_approval' && (
                  <button className="action-button waiting-button" disabled>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Waiting for Response
                  </button>
                )}

                {/* View Shipping Details Button - Only for product-based confirmed projects with delivery address, and product not yet shipped */}
                {effectiveCreatorData.status === 'confirmed' && 
                 effectiveCreatorData.deliveryAddress && 
                 effectiveCreatorData.status !== 'product_sent' && 
                 (effectiveCreatorData.productType === 'product' || effectiveCreatorData.serviceOrProduct === 'Product') && (
                  <button 
                    className="action-button shipping-details-button"
                    onClick={() => setShowShippingDetailsModal(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    View Shipping Details
                  </button>
                )}

                {effectiveCreatorData.status === 'rejected' && (
                  <button className="action-button remove-button">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Remove & Refund
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
        )}
      </div>
      
      {/* Shipping Details Modal */}
      <ShippingDetailsModal
        isOpen={showShippingDetailsModal}
        onClose={() => setShowShippingDetailsModal(false)}
        creatorData={effectiveCreatorData}
        onProductShipped={handleProductSent}
      />

      {/* Extension Request Modal */}
      <ExtensionRequestModal
        isOpen={showExtensionRequestModal}
        onClose={() => setShowExtensionRequestModal(false)}
        creatorData={effectiveCreatorData ? {
          ...effectiveCreatorData,
          // Ensure projectId is always present - use from original creatorData if missing
          projectId: effectiveCreatorData.projectId || creatorData?.projectId || null,
          // Ensure graceRequest is preserved
          graceRequest: effectiveCreatorData.graceRequest || creatorData?.graceRequest || null
        } : creatorData}
        onExtensionResponse={(status, approvedDays) => {
          // Refresh creator data after response
          if (effectiveCreatorData) {
            // The brief listener will automatically update the data
            console.log(`Extension request ${status}${approvedDays ? ` (${approvedDays} days)` : ''}`);
          }
        }}
      />
    </div>
  );
}

export default CreatorProgressModal;
